import { Link } from "gatsby";
import React, { PropsWithoutRef } from "react";
import styled from "styled-components";
import tw from "twin.macro";

import logoMarkSrc from "../../assets/images/logo-mark.svg";
import {
  features,
  forIndustries,
  forTeams,
  solutions,
} from "../layout/nav/navItems";
import SocialLinks from "../layout/partials/SocialLinks";
import Container from "./Container";
import Image from "./Image";

const Wrapper = styled.div`
  margin-top: -18px;
`;

const FooterBlocks = styled.div`
  ${tw`relative grid grid-cols-5 text-[14px] leading-[23px] py-[48px] pl-[106px] text-theme-gray-7`}
  gap: 20px;

  @media (max-width: 960px) {
    ${tw`grid-cols-4`}
  }

  @media (max-width: 850px) {
    ${tw`grid-cols-3`}
  }

  @media (max-width: 650px) {
    ${tw`grid-cols-2`}
  }

  @media (max-width: 520px) {
    ${tw`pt-[126px] pl-0`}
  }
`;

const FooterLogo = styled.div`
  ${tw`absolute top-[48px] left-0`}
`;

const FooterBlock = styled.div`
  ul li {
    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }

  a {
    ${tw`text-theme-gray-7`}
  }

  // @media (max-width: 983px) {
  //   ${tw`absolute top-0 left-0`}
  // }
`;

const Title = styled.div`
  ${tw`font-semibold uppercase mb-[6px]`}
`;

const FooterBottom = styled.div`
  ${tw`flex justify-between pb-2`}

  @media (max-width: 960px) {
    ${tw`flex-col items-center`}
  }
`;

const Icons = styled.div`
  ${tw`flex items-center`}
  gap: 30px;
`;

const StripeVerifiedPartner = styled.div``;

const Legal = styled.div`
  ${tw`text-[14px] leading-[23px] text-theme-gray-3 py-[14px]`}

  a {
    ${tw`text-theme-gray-3`}

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer: React.FunctionComponent<PropsWithoutRef<{}>> = (props) => (
  <Wrapper {...props}>
    <Container>
      <FooterBlocks>
        <FooterLogo>
          <img src={logoMarkSrc} alt="ProsperStack" width={42} height={38} />
        </FooterLogo>
        <FooterBlock>
          <Title>Solutions</Title>
          <ul className="list-reset">
            {solutions.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.title}</Link>
              </li>
            ))}
          </ul>

          <Title>Explore</Title>
          <ul className="list-reset">
            <li>
              <Link to="/pricing/">Pricing</Link>
            </li>
            <li>
              <Link to="/roi-calculator/">ROI Calculator</Link>
            </li>
            <li>
              <Link to="/integrations/">Integrations</Link>
            </li>
            <li>
              <Link to="/customers/">Case Studies</Link>
            </li>
          </ul>
        </FooterBlock>
        <FooterBlock>
          <Title>For Teams</Title>
          <ul className="list-reset">
            {forTeams.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.title}</Link>
              </li>
            ))}
          </ul>

          <Title>For Industries</Title>
          <ul className="list-reset">
            {forIndustries.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.title}</Link>
              </li>
            ))}
          </ul>
        </FooterBlock>
        <FooterBlock>
          <Title>Features</Title>
          <ul className="list-reset">
            {features.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.title}</Link>
              </li>
            ))}
          </ul>
        </FooterBlock>
        <FooterBlock>
          <Title>Resources</Title>
          <ul className="list-reset">
            <li>
              <Link to="/blog/">Blog</Link>
            </li>
            <li>
              <a
                href="https://docs.prosperstack.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Documentation
              </a>
            </li>
            <li>
              <a
                href="https://developers.prosperstack.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                API Reference
              </a>
            </li>
            <li>
              <a
                href="https://status.prosperstack.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                System Status
              </a>
            </li>
            <li>
              <a
                href="https://changelog.prosperstack.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Changelog
              </a>
            </li>
          </ul>
        </FooterBlock>
        <FooterBlock>
          <Title>Company</Title>
          <ul className="list-reset">
            <li>
              <Link to="/about-us/">About Us</Link>
            </li>
            <li>
              <a
                href="https://careers.prosperstack.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Careers
              </a>
            </li>
            <li>
              <Link to="/press/">Press</Link>
            </li>
          </ul>
        </FooterBlock>
      </FooterBlocks>
      <FooterBottom>
        <Icons>
          <SocialLinks grayIcons={true} />
          <StripeVerifiedPartner>
            <a
              href="https://stripe.com/partners/prosperstack"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Stripe Verified Partner"
            >
              <Image
                src={
                  require("./../../assets/images/stripe-verified-partner-gray.svg")
                    .default
                }
                alt=""
              />
            </a>
          </StripeVerifiedPartner>
        </Icons>
        <Legal>
          <Link to="/terms-of-service/">Terms of Service</Link> &bull;{" "}
          <Link to="/privacy-policy/">Privacy Policy</Link> &bull;{" "}
          <Link to="/data-privacy-framework/">Data Privacy Framework</Link>{" "}
          &bull; <Link to="/dmca-notice/">DMCA Notice</Link> &bull;{" "}
          <Link to="/cookie-policy/">Cookie Policy</Link> &bull;{" "}
          <Link to="/dpa/">DPA</Link> &bull;{" "}
          <Link to="/subprocessors/">Subprocessors</Link>
        </Legal>
      </FooterBottom>
    </Container>
  </Wrapper>
);

export default Footer;
